// src/features/summary/SummarySlice.ts

import {
  createSlice,
  createAsyncThunk,
  PayloadAction
} from "@reduxjs/toolkit";

import {
  getAssetSummary,
  getPlantsBySite,
  getPinnedAssetSummary,
  pinAssetApi,
  PinPayload,
  PaginatedAssetSummaryResponse,
  parsePaginatedSummaryResponse
} from "../../Services/serviceURL";
import { RootState } from "../../store";

// --- TYPES ---


// Helper function to safely parse localStorage data
const getPinnedAssetsFromStorage = () => {
  try {
    const localStorageData = localStorage.getItem('pinnedAssetsMapByCustomer');
    return localStorageData ? JSON.parse(localStorageData) : {};
  } catch (error) {
    console.error('Error parsing pinned assets from localStorage:', error);
    return {};
  }
};

const parsedData = getPinnedAssetsFromStorage();

export const buildSummaryCacheKey = (
  endpoint: 'assets' | 'pinned',
  params: {
    customerId?: string;
    siteId?: string;
    plantId?: string;
    pageNumber: number;
    pageSize: number;
  }
): string => {
  const { customerId = '', siteId = '', plantId = '', pageNumber, pageSize } = params;
  return `${endpoint}|${customerId}|${siteId}|${plantId}|${pageNumber}|${pageSize}`;
};

export interface PinnedAssetEntry {
  assetId: string;
  pinnedAssetId: string;
  // (add any other fields your API returns)
}

export interface SummaryState {
  assetSummary: any[];
  totalCount: number;
  totalPages: number;
  assetSummaryPageCache: Record<string, PaginatedAssetSummaryResponse>;
  plants: any[];
  pinnedAssetsMap: Record<string, PinnedAssetEntry>;
  loading: boolean;
  plantsLoading: boolean;
  pinnedLoading: boolean;
  pinningAssetId: string | null;
  error: string | null;
  plantsError: string | null;
  pinnedError: string | null;
  pinnedAssetsMapByCustomer: Record<string, Record<string, PinnedAssetEntry>>;
}

// --- INITIAL STATE ---

const initialState: SummaryState = {
  assetSummary: [],
  totalCount: 0,
  totalPages: 0,
  assetSummaryPageCache: {},
  plants: [],
  pinnedAssetsMap: {},
  loading: false,
  plantsLoading: false,
  pinnedLoading: false,
  pinningAssetId: null,
  error: null,
  plantsError: null,
  pinnedError: null,
  pinnedAssetsMapByCustomer: parsedData ,
};

// --- ASYNC THUNKS ---

// 1) summary list
export const fetchAssetSummary = createAsyncThunk<
  PaginatedAssetSummaryResponse,
  {
    customerId?: string;
    siteId?: string;
    plantId?: string;
    pageNumber: number;
    pageSize: number;
  },
  { rejectValue: string }
>(
  "summary/fetchAssetSummary",
  async (params, { rejectWithValue }) => {
    try {
      const { customerId, siteId, plantId } = params;

      const fetchPageSize = 500;
      let pageNumber = 1;
      let totalPages = 1;
      let totalCount = 0;
      const allItems: PaginatedAssetSummaryResponse["items"] = [];

      do {
        const resp = await getAssetSummary(pageNumber, fetchPageSize, customerId, siteId, plantId);
        const parsed = parsePaginatedSummaryResponse(resp.data);
        allItems.push(...parsed.items);
        totalCount = parsed.totalCount;
        totalPages = parsed.totalPages || 1;
        pageNumber += 1;
      } while (pageNumber <= totalPages);

      return {
        items: allItems,
        totalCount,
        pageNumber: 1,
        pageSize: allItems.length,
        totalPages: 1,
      };
    } catch {
      return rejectWithValue("Failed to fetch asset summary");
    }
  }
);

// 2) plants by site
export const fetchPlantsBySite = createAsyncThunk<
  any[],
  string,
  { rejectValue: string }
>(
  "summary/fetchPlantsBySite",
  async (siteId, { rejectWithValue }) => {
    try {
      const resp = await getPlantsBySite(siteId);
      return resp.data.Data as any[];
    } catch {
      return rejectWithValue("Failed to fetch plants");
    }
  }
);

// 3) pinned summary
export const fetchPinnedAssets = createAsyncThunk<
  PinnedAssetEntry[],
  {
    customerId?: string;
    siteId?: string;
    plantId?: string;
    pageNumber?: number;
    pageSize?: number;
  } | undefined,
  { rejectValue: string }
>(
  "summary/fetchPinnedAssets",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { customerId, siteId, plantId, pageNumber = 1, pageSize = 500 } = params;
      const resp = await getPinnedAssetSummary(pageNumber, pageSize, customerId, siteId, plantId);
      const parsed = parsePaginatedSummaryResponse(resp.data);
      return parsed.items.map((item) => ({
        ...item,
        pinnedAssetId: item.assetId,
      }));
    } catch {
      return rejectWithValue("Failed to load pinned assets");
    }
  }
);

// 4) toggle pin/unpin
export const pinAsset = createAsyncThunk<
  {
    assetId: string;
    isPinned: boolean;
    success: boolean;
    message: string;
  },
  { assetId: string; isPinned: boolean },
  { rejectValue: string }
>(
  "summary/pinAsset",
  async ({ assetId, isPinned }, { dispatch, rejectWithValue }) => {
    try {
      const payload: PinPayload = {
        assetId,
        isPinned
      };
      
      const { data } = await pinAssetApi(payload);

      // Return the simplified response data
      return {
        assetId: assetId,
        isPinned: isPinned,
        success: data.Success,
        message: data.Message
      };
    } catch (error) {
      console.error('Pin/Unpin Error:', error);
      return rejectWithValue("Failed to toggle pin status");
    }
  }
);

// --- SLICE ---

const summarySlice = createSlice({
  name: "summary",
  initialState,
  reducers: {
    // rarely needed, but available if you want to manually override
    setPinnedAssetsMap(
      state,
      action: PayloadAction<Record<string, PinnedAssetEntry>>
    ) {
      state.pinnedAssetsMap = action.payload;
    },
    setAssetSummary(
      state,
      action: PayloadAction<any[]>
    ) {
      state.assetSummary = action.payload;
      state.loading = false;
    },
    loadAssetSummaryFromCache: (state, action: PayloadAction<string>) => {
      const cached = state.assetSummaryPageCache[action.payload];
      if (cached) {
        state.assetSummary = cached.items;
        state.totalCount = cached.totalCount;
        state.totalPages = cached.totalPages;
        state.loading = false;
        state.error = null;
      }
    },
    clearAssetSummaryPageCache: (state) => {
      state.assetSummaryPageCache = {};
    },
    setPinnedAssetsMapByCustomer: (state, action) => {
  const { customerId, pinnedAssets } = action.payload;
  state.pinnedAssetsMapByCustomer[customerId] = pinnedAssets;
  
  // Persist to localStorage
  try {
    localStorage.setItem('pinnedAssetsMapByCustomer', JSON.stringify(state.pinnedAssetsMapByCustomer));
  } catch (error) {
    console.error('Failed to persist pinned assets to localStorage:', error);
  }
},
loadPinnedAssetsFromStorage: (state) => {
  const storedData = getPinnedAssetsFromStorage();
  state.pinnedAssetsMapByCustomer = storedData;
},



  },
  extraReducers: (builder) => {
    // fetchAssetSummary
    builder
      .addCase(fetchAssetSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssetSummary.fulfilled, (state, { payload, meta }) => {
        state.loading = false;
        state.assetSummary = payload.items;
        state.totalCount = payload.totalCount;
        state.totalPages = payload.totalPages;
        const cacheKey = buildSummaryCacheKey('assets', meta.arg);
        state.assetSummaryPageCache[cacheKey] = payload;
      })
      .addCase(fetchAssetSummary.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload ?? "Unknown error";
      });

    // fetchPlantsBySite
    builder
      .addCase(fetchPlantsBySite.pending, (state) => {
        state.plantsLoading = true;
        state.plantsError = null;
      })
      .addCase(fetchPlantsBySite.fulfilled, (state, { payload }) => {
        state.plantsLoading = false;
        state.plants = payload;
      })
      .addCase(fetchPlantsBySite.rejected, (state, { payload }) => {
        state.plantsLoading = false;
        state.plantsError = payload ?? "Unknown error";
      });

    // fetchPinnedAssets
    builder
      .addCase(fetchPinnedAssets.pending, (state) => {
        state.pinnedLoading = true;
        state.pinnedError = null;
      })
      .addCase(fetchPinnedAssets.fulfilled, (state, { payload, meta }) => {
        state.pinnedLoading = false;
        const map: Record<string, PinnedAssetEntry> = {};
        const fetchedByCustomer: Record<string, Record<string, PinnedAssetEntry>> = {};

        payload.forEach((item) => {
          const assetId = item.assetId || (item as { id?: string }).id;
          if (!assetId) return;

          map[assetId] = item;

          const customerId = (item as { customerId?: string }).customerId;
          if (customerId) {
            if (!fetchedByCustomer[customerId]) {
              fetchedByCustomer[customerId] = {};
            }
            fetchedByCustomer[customerId][assetId] = item;
          }
        });

        state.pinnedAssetsMap = map;

        const scopedCustomerId = meta.arg?.customerId;
        if (scopedCustomerId) {
          state.pinnedAssetsMapByCustomer[scopedCustomerId] = fetchedByCustomer[scopedCustomerId] || {};
        } else {
          state.pinnedAssetsMapByCustomer = fetchedByCustomer;
        }

        try {
          localStorage.setItem('pinnedAssetsMapByCustomer', JSON.stringify(state.pinnedAssetsMapByCustomer));
        } catch (error) {
          console.error('Failed to persist pinned assets to localStorage:', error);
        }
      })
      .addCase(fetchPinnedAssets.rejected, (state, { payload }) => {
        state.pinnedLoading = false;
        state.pinnedError = payload ?? "Unknown error";
      });

    // pinAsset
    builder
      .addCase(pinAsset.pending, (state, { meta }) => {
        state.pinningAssetId = meta.arg.assetId;
      })
      .addCase(pinAsset.fulfilled, (state, { payload }) => {
        state.pinningAssetId = null;
        // Log the operation result for debugging
        // pinnedAssetsMap updated by fetchPinnedAssets
      })
      .addCase(pinAsset.rejected, (state, { payload }) => {
        state.pinningAssetId = null;
        state.error = payload ?? "Pin/unpin failed";
      });
  }
});

export const {
  setPinnedAssetsMap,
  loadPinnedAssetsFromStorage,
  setAssetSummary,
  loadAssetSummaryFromCache,
  clearAssetSummaryPageCache,
} = summarySlice.actions;
export const selectPinnedAssetsMap = (
  state: RootState,
  customerId: string
): PinnedAssetsMap => {
  return state.summary.pinnedAssetsMapByCustomer[customerId] || ({} as PinnedAssetsMap);
};

export const { setPinnedAssetsMapByCustomer } = summarySlice.actions;
export type PinnedAssetsMap = Record<string, PinnedAssetEntry>;




export default summarySlice.reducer;